import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/features/auth/providers/AuthProvider';
import useUpdateProfile from '@/features/user/hooks/useUpdateProfile';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Booking } from '@/features/booking/types';
import type { Office, PlacedDesk, PlacedMeetingRoom } from '@/features/office/types';
import { findFloorForResource, resolveOfficeFloors } from '@/features/office/utils/floors';
import useGetPreferredOffice from '@/shared/hooks/useGetPreferredOffice';
import useGetOffice from '@/features/office/hooks/useGetOffice';
import { BookedResource } from '@/features/booking/components/BookingList';
import { UnifiedBookingList } from '@/features/booking/components/UnifiedBookingList';
import { OfficeSelector } from '@/features/office/components/OfficeSelector';
import useGetBookings from '@/features/booking/hooks/useGetBookings';
import useGetMeetingRoomBookings from '@/features/office/hooks/useGetMeetingRoomBookings';
import useGetAllBookings from '@/features/booking/hooks/useGetAllBookings';
import { FacilitiesNotificationPrompt } from '@/features/booking/components/FacilitiesNotificationPrompt';
import { auth } from '@/core/firebase/config';
import { User as AppUser, NotificationPreferences } from '@/features/auth/types';
import useGetUsersList from '@/features/user/hooks/useGetUsersList';
import { ProfileContentSkeleton } from '@/shared/components/feedback/skeletons';
import { ErrorState } from '@/shared/components/feedback';
import { AdminHeaderSettingsButton, AdminPageLayout } from '@/shared/components/ui/AdminHeader';
import { ProfileHeader } from '@/features/user/components/ProfileHeader';
import { ProfileSettingsPanel } from '@/features/user/components/ProfileSettingsPanel';
import { Dialog, DialogContent } from '@/shared/components/ui/dialog';
import { toast } from 'sonner';
import useNotificationPermission from '@/features/notifications/hooks/useNotificationPermission';
import { WrapProfileButton } from '@/features/wrap/components/WrapProfileButton';
import useWrapSeason from '@/features/wrap/hooks/useWrapSeason';

export const Route = createFileRoute('/profile')({
  component: ProfilePage,
  validateSearch: (search: Record<string, unknown>) => ({
    settings: (search.settings as string) || undefined,
  }),
});

function ProfilePage() {
  return <ProfileContent />;
}

function ProfileContent() {
  const { user } = useAuth();
  const updateProfile = useUpdateProfile({});
  const { users } = useGetUsersList();
  const { canAsk, requestPermission, permission } = useNotificationPermission({
    userId: user?.uid ?? null,
  });
  const { settings: settingsParam } = useSearch({ from: '/profile' });
  const navigate = useNavigate();
  const wrapSeason = useWrapSeason(user?.uid);
  const { preferredOffice } = useGetPreferredOffice(auth.currentUser?.uid);
  const [selectedOfficeId, setSelectedOfficeId] = useState<string | null>(null);

  // State for preferences
  const [selectedTreats, setSelectedTreats] = useState<string[]>([]);
  const [selectedDietary, setSelectedDietary] = useState<string[]>([]);
  const [newTreatInput, setNewTreatInput] = useState<string>('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(!!settingsParam);
  const [settingsTab, setSettingsTab] = useState<string>(settingsParam || 'general');
  const [isSaving, setIsSaving] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreferences>({});
  const [localIsRemoteWorker, setLocalIsRemoteWorker] = useState(false);

  useEffect(() => {
    if (preferredOffice && !selectedOfficeId) {
      setSelectedOfficeId(preferredOffice);
    }
  }, [preferredOffice, selectedOfficeId]);

  const suggestedTreats = useMemo(() => {
    const treatCounts = new Map<string, { count: number; display: string }>();

    users.forEach(u => {
      u.treatsPreference?.forEach(t => {
        const normalized = t.trim().toLowerCase();
        if (!normalized) return;

        if (!treatCounts.has(normalized)) {
          treatCounts.set(normalized, { count: 0, display: t.trim() });
        }

        const entry = treatCounts.get(normalized)!;
        entry.count++;
        // Prefer capitalized version if available
        if (
          t.trim()[0] === t.trim()[0].toUpperCase() &&
          entry.display[0] !== entry.display[0].toUpperCase()
        ) {
          entry.display = t.trim();
        }
      });
    });

    // Remove treats the user already has
    const userTreatsNormalized = new Set(selectedTreats.map(t => t.trim().toLowerCase()));

    return Array.from(treatCounts.values())
      .filter(item => !userTreatsNormalized.has(item.display.toLowerCase()))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10) // Limit to top 10 suggestions
      .map(item => item.display);
  }, [users, selectedTreats]);

  const handleSuggestTreat = (treat: string) => {
    if (selectedTreats.length >= 2) return;
    if (!selectedTreats.includes(treat)) {
      const newSelection = [...selectedTreats, treat];
      setSelectedTreats(newSelection);
    }
  };

  const resetPreferences = () => {
    const appUser = user as unknown as AppUser;
    if (appUser) {
      setSelectedTreats(appUser.treatsPreference || []);
      setSelectedDietary(appUser.dietaryRestrictions || []);
      setNotificationPrefs(appUser.notificationPreferences || {});
      setLocalIsRemoteWorker(appUser.isRemoteWorker ?? false);
    }
  };

  // Sync state with user data
  useEffect(() => {
    resetPreferences();
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    setIsSaving(true);
    const appUser = user as unknown as AppUser;
    try {
      await updateProfile({
        ...appUser,
        treatsPreference: selectedTreats,
        dietaryRestrictions: selectedDietary,
        isRemoteWorker: localIsRemoteWorker,
        notificationPreferences: notificationPrefs,
      });
      setIsSettingsOpen(false);
      setSettingsTab('general');
      if (settingsParam) {
        navigate({ to: '/profile', search: { settings: undefined }, replace: true });
      }
      toast.success('Settings saved');
    } catch (error) {
      console.error('Failed to save settings', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    resetPreferences();
    setIsSettingsOpen(false);
    setSettingsTab('general');
    // Clear the search param if present
    if (settingsParam) {
      navigate({ to: '/profile', search: { settings: undefined }, replace: true });
    }
  };

  const handleSettingsOpenChange = (open: boolean) => {
    if (!open) {
      handleCancel();
    } else {
      setIsSettingsOpen(true);
    }
  };

  const handleReorder = (newOrder: string[]) => {
    setSelectedTreats(newOrder);
  };

  const handleRemoveTreat = (value: string) => {
    const newSelection = selectedTreats.filter(item => item !== value);
    setSelectedTreats(newSelection);
  };

  const handleAddTreat = () => {
    if (!newTreatInput.trim()) return;
    if (selectedTreats.length >= 2) {
      return;
    }

    const treatToAdd = newTreatInput.trim();
    if (!selectedTreats.includes(treatToAdd)) {
      const newSelection = [...selectedTreats, treatToAdd];
      setSelectedTreats(newSelection);
      setNewTreatInput('');
    }
  };

  const handleDietaryToggle = (value: string) => {
    let newSelection: string[];
    if (selectedDietary.includes(value)) {
      newSelection = selectedDietary.filter(item => item !== value);
    } else {
      newSelection = [...selectedDietary, value];
    }
    setSelectedDietary(newSelection);
  };

  const handleTreatInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleAddTreat();
    }
  };

  if (!user) return null;
  const appUser = user as unknown as AppUser;

  const handleNotificationToggle = (key: keyof NotificationPreferences, checked: boolean) => {
    setNotificationPrefs(prev => ({ ...prev, [key]: checked }));
  };

  const officeSelector = (
    <OfficeSelector
      selectedValue={selectedOfficeId}
      onChange={setSelectedOfficeId}
      autoSelectFirst={false}
      filterByManagedOffices={false}
    />
  );

  const headerActions = (
    <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
      {wrapSeason.isOpen && (
        <WrapProfileButton year={wrapSeason.year} className="w-full md:w-auto" />
      )}
      {officeSelector}
    </div>
  );

  return (
    <AdminPageLayout
      header={
        <ProfileHeader
          user={appUser}
          rightContent={headerActions}
          settings={<AdminHeaderSettingsButton onClick={() => handleSettingsOpenChange(true)} />}
        />
      }
    >
      <Dialog open={isSettingsOpen} onOpenChange={handleSettingsOpenChange}>
        <DialogContent className="sm:max-w-[680px] h-[75vh] max-h-[720px] flex flex-col p-0 gap-0">
          <ProfileSettingsPanel
            tab={settingsTab}
            onTabChange={setSettingsTab}
            isRemoteWorker={localIsRemoteWorker}
            onRemoteWorkerChange={setLocalIsRemoteWorker}
            selectedDietary={selectedDietary}
            onDietaryToggle={handleDietaryToggle}
            selectedTreats={selectedTreats}
            onReorderTreats={handleReorder}
            onRemoveTreat={handleRemoveTreat}
            newTreatInput={newTreatInput}
            onNewTreatInputChange={setNewTreatInput}
            onAddTreat={handleAddTreat}
            onTreatInputKeyDown={handleTreatInputKeyDown}
            suggestedTreats={suggestedTreats}
            onSuggestTreat={handleSuggestTreat}
            notificationPrefs={notificationPrefs}
            onNotificationToggle={handleNotificationToggle}
            permission={permission}
            canAsk={canAsk}
            onRequestPermission={async () => {
              await requestPermission();
              localStorage.setItem('robinhoot_notification_banner_dismissed', String(Date.now()));
            }}
            onCancel={handleCancel}
            onSave={handleSave}
            isSaving={isSaving}
          />
        </DialogContent>
      </Dialog>
      <BookingsSection
        selectedOfficeId={selectedOfficeId}
        isRemoteWorker={appUser.isRemoteWorker ?? false}
      />
    </AdminPageLayout>
  );
}

function BookingsSection({
  selectedOfficeId,
  isRemoteWorker,
}: {
  selectedOfficeId: string | null;
  isRemoteWorker: boolean;
}) {
  if (!selectedOfficeId) {
    return <ProfileContentSkeleton />;
  }

  return <BookingListWrapper selectedOfficeId={selectedOfficeId} isRemoteWorker={isRemoteWorker} />;
}

function BookingListWrapper({
  selectedOfficeId,
  isRemoteWorker,
}: {
  selectedOfficeId: string;
  isRemoteWorker: boolean;
}) {
  const { office, loading } = useGetOffice(selectedOfficeId);

  if (loading) {
    return <ProfileContentSkeleton />;
  }

  if (!office) {
    return (
      <ErrorState
        title="Office not found"
        description="The selected office could not be found. Please try selecting a different office."
      />
    );
  }

  return <BookingListContent office={office} isRemoteWorker={isRemoteWorker} />;
}

function BookingListContent({
  office,
  isRemoteWorker,
}: {
  office: Office;
  isRemoteWorker: boolean;
}) {
  const officeTimezone = office.timezone || 'UTC';
  const { bookings: allDeskBookings } = useGetBookings(
    office.id,
    undefined,
    undefined,
    officeTimezone
  );
  const { bookings: allMeetingRoomBookings } = useGetMeetingRoomBookings(
    office.id,
    undefined,
    undefined,
    officeTimezone
  );
  const { overflowBookings } = useGetAllBookings(office.id, undefined, undefined, officeTimezone);
  const [facilitiesPromptOpen, setFacilitiesPromptOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<BookedResource | null>(null);

  const enableFacilitiesNotification = office.layout?.enableFacilitiesNotification ?? false;
  const showNotifyButton = isRemoteWorker && enableFacilitiesNotification;

  const currentUserEmail = auth.currentUser?.email;
  const myDeskBookings = useMemo(
    () => allDeskBookings.filter(value => value.userEmail === currentUserEmail),
    [allDeskBookings, currentUserEmail]
  );

  const myMeetingRoomBookings = useMemo(
    () => allMeetingRoomBookings.filter(value => value.userEmail === currentUserEmail),
    [allMeetingRoomBookings, currentUserEmail]
  );

  const myOverflowBookings = useMemo(
    () => overflowBookings.filter(value => value.userEmail === currentUserEmail),
    [overflowBookings, currentUserEmail]
  );

  // Helper to extract the raw bookingId and dateTimestamp from the composite id
  const parseBookingCompositeId = (compositeId: string) => {
    const parts = compositeId.split(':');
    if (parts.length === 2) {
      return { dateTimestamp: parts[0], bookingId: parts[1] };
    }
    return { dateTimestamp: '', bookingId: compositeId };
  };

  const data = useMemo((): BookedResource[] => {
    const floors = resolveOfficeFloors(office);
    const floorNameFor = (resourceId: string) =>
      floors.length > 1 ? findFloorForResource(floors, resourceId)?.name : undefined;

    const deskData: BookedResource[] = myDeskBookings
      .filter((booked: Booking) => booked.resourceId !== undefined)
      .map((booked: Booking): BookedResource => {
        const deskId = booked.resourceId!;
        const floor = findFloorForResource(floors, deskId);
        const deskData = floor?.layout.placedDesks?.[deskId];
        let deskName = deskId; // Fallback to ID

        if (deskData) {
          const completeDesk: PlacedDesk = { ...deskData, id: deskId };
          if (completeDesk.label && completeDesk.label.trim() !== '') {
            deskName = completeDesk.label;
          } else {
            deskName = 'Unlabelled Desk';
          }
        }

        const { dateTimestamp, bookingId } = parseBookingCompositeId(booked.id);

        return {
          id: deskId,
          date: booked.date,
          name: deskName,
          floorName: floorNameFor(deskId),
          type: 'DESK' as const,
          startTime: booked.startTime,
          endTime: booked.endTime,
          officeId: office.id,
          officeTimezone,
          isGuest: booked.isGuest,
          guestName: booked.guestName,
          userName: booked.userName,
          facilitiesNotified: booked.facilitiesNotified,
          bookingId,
          dateTimestamp,
        };
      });

    const meetingRoomData: BookedResource[] = myMeetingRoomBookings
      .filter((booked: Booking) => booked.resourceId !== undefined)
      .map((booked: Booking): BookedResource => {
        const meetingRoomId = booked.resourceId!;
        const floor = findFloorForResource(floors, meetingRoomId);
        const meetingRoomData = floor?.layout.placedMeetingRooms?.[meetingRoomId];
        let meetingRoomName = meetingRoomId; // Fallback to ID
        let roomType: 'PHONE_BOOTH' | 'MEETING_ROOM' = 'MEETING_ROOM';

        if (meetingRoomData) {
          const completeMeetingRoom: PlacedMeetingRoom = { ...meetingRoomData, id: meetingRoomId };
          if (completeMeetingRoom.label && completeMeetingRoom.label.trim() !== '') {
            meetingRoomName = completeMeetingRoom.label;
          } else {
            meetingRoomName = 'Unlabelled Room';
          }
          roomType = completeMeetingRoom.type;
        }

        const { dateTimestamp, bookingId } = parseBookingCompositeId(booked.id);

        return {
          id: meetingRoomId,
          date: booked.date,
          name: meetingRoomName,
          floorName: floorNameFor(meetingRoomId),
          type: 'MEETING_ROOM' as const,
          startTime: booked.startTime,
          endTime: booked.endTime,
          roomType,
          officeId: office.id,
          officeTimezone,
          isGuest: booked.isGuest,
          guestName: booked.guestName,
          userName: booked.userName,
          facilitiesNotified: booked.facilitiesNotified,
          bookingId,
          dateTimestamp,
        };
      });

    const overflowData: BookedResource[] = myOverflowBookings.map(
      (booked: Booking): BookedResource => {
        const { dateTimestamp, bookingId } = parseBookingCompositeId(booked.id);

        return {
          id: booked.id,
          date: booked.date,
          name: 'Desk-less Booking',
          type: 'OVERFLOW' as const,
          startTime: booked.startTime,
          endTime: booked.endTime,
          officeId: office.id,
          officeTimezone,
          isGuest: booked.isGuest,
          guestName: booked.guestName,
          userName: booked.userName,
          facilitiesNotified: booked.facilitiesNotified,
          bookingId,
          dateTimestamp,
        };
      }
    );

    // Combine and sort by date (most recent first)
    const allBookings = [...deskData, ...meetingRoomData, ...overflowData];
    return allBookings.sort((a, b) => {
      // Handle timestamp strings properly
      const timestampA = isNaN(Number(a.date)) ? Date.parse(a.date) : Number(a.date);
      const timestampB = isNaN(Number(b.date)) ? Date.parse(b.date) : Number(b.date);
      return timestampB - timestampA;
    });
  }, [
    myDeskBookings,
    myMeetingRoomBookings,
    myOverflowBookings,
    office.layout,
    office.floors,
    office.id,
    officeTimezone,
  ]);

  const handleNotifyFacilities = useCallback(async (resource: BookedResource): Promise<boolean> => {
    if (!resource.bookingId || !resource.dateTimestamp) return false;
    // Open confirmation dialog instead of sending immediately
    setSelectedResource(resource);
    setFacilitiesPromptOpen(true);
    return true;
  }, []);

  return (
    <>
      <UnifiedBookingList
        data={data}
        showNotifyButton={showNotifyButton}
        onNotifyFacilities={handleNotifyFacilities}
        notifyingBookingId={null}
      />

      {selectedResource && (
        <FacilitiesNotificationPrompt
          open={facilitiesPromptOpen}
          onOpenChange={open => {
            setFacilitiesPromptOpen(open);
            if (!open) setSelectedResource(null);
          }}
          bookingId={selectedResource.bookingId ?? ''}
          officeId={selectedResource.officeId}
          dateTimestamp={selectedResource.dateTimestamp ?? ''}
          officeTimezone={officeTimezone}
          userEmail={auth.currentUser?.email || ''}
          deskBookings={myDeskBookings}
          hideProfileHint
        />
      )}
    </>
  );
}
